import type { FormVersion } from "../state/formVersion";
import { useFormVersionDispatch, useFormVersionState } from "../state/formVersion";
import { makePersisted } from "@solid-primitives/storage";
import type { Accessor, Setter, Signal } from "solid-js";
import { createEffect, createSignal, on } from "solid-js";
import { trackStore } from "@solid-primitives/deep";
import { initialized } from "../utils/initializedUtil";
import { isNil } from "lodash-es";
import type { SetStoreFunction } from "solid-js/store";
import logger from "../utils/logger";
import posthog from "posthog-js";
import { POSTHOG_EVENTS } from "./PostHogService";
import { deserializeFormVersion, serializeFormVersion } from "../utils/serializationUtil";
import createCompressedStorage from "../utils/createCompressedStorage.ts";

let formVersionStore: FormVersion | null = null;
let setFormVersionStore: SetStoreFunction<FormVersion> | null = null;

let localVersion: Accessor<FormVersion | null> | null = null;
let setLocalVersion: Setter<FormVersion | null> | null = null;

const getFormVersionStore = () => initialized(formVersionStore, "formVersionStore");
const getSetFormVersionStore = () => initialized(setFormVersionStore, "setFormVersionStore");
const getLocalVersion = () => initialized(localVersion, "localVersion");
const getSetLocalVersion = () => initialized(setLocalVersion, "setLocalVersion");

let watcherEnabled = false;

const init = () => {
  formVersionStore = useFormVersionState();
  setFormVersionStore = useFormVersionDispatch();
  const [localFormVersion, setLocalFormVersion] = makePersisted<FormVersion | null, Signal<FormVersion | null>>(
    createSignal<FormVersion | null>(null),
    {
      name: "lastFormVersion",
      serialize: serializeFormVersion,
      deserialize: deserializeFormVersion,
      // Use localForage instead of localStorage. Using localStorage, we can only store up to 5MiB (https://developer.mozilla.org/en-US/docs/Web/API/Storage_API/Storage_quotas_and_eviction_criteria#web_storage).
      // LocalForage uses IndexedDB, which supports storing at least multiple tens of GiBs (https://developer.mozilla.org/en-US/docs/Web/API/Storage_API/Storage_quotas_and_eviction_criteria#other_web_technologies).
      storage: createCompressedStorage(),
    },
  );

  localVersion = localFormVersion;
  setLocalVersion = setLocalFormVersion;

  if (shouldRestore()) {
    posthog.capture(POSTHOG_EVENTS.FORM_VERSION_RESTORE_REQUIRED);
    return;
  }

  enableWatcher();
};

const enableWatcher = () => {
  if (watcherEnabled) {
    return;
  }

  watcherEnabled = true;
  createEffect(
    on(
      () => trackStore(getFormVersionStore()),
      (formVersion) => {
        getSetLocalVersion()(formVersion);
      },
    ),
  );
};

const shouldRestore = () => {
  try {
    const currentLocalVersion = getLocalVersion()();
    const formVersion = getFormVersionStore();

    if (isNil(currentLocalVersion)) {
      return false;
    }

    // When no last edit is saved, but lastUpdated is set in localstorage, possible crash
    return (
      currentLocalVersion.id.equals(formVersion.id) &&
      isNil(formVersion.lastUpdatedAt) &&
      !isNil(currentLocalVersion.lastUpdatedAt) &&
      isUpdatedLater(new Date(currentLocalVersion.lastUpdatedAt), formVersion.meta.lastUpdated)
    );
  } catch (e) {
    logger.error("An error occurred during restore decision", e);
    return false;
  }
};

const isUpdatedLater = (localTimestamp: Date, remoteTimestamp: Date) =>
  localTimestamp.getTime() > remoteTimestamp.getTime();

const reset = () => {
  cleanup();
  enableWatcher();
};

const cleanup = () => {
  getSetLocalVersion()(null);
};

const restore = () => {
  const localVersion = getLocalVersion()();
  if (isNil(localVersion)) {
    // Something is wrong. Reset to not block the user.
    reset();
    return;
  }

  getSetFormVersionStore()(localVersion);
  reset();
};

const FormVersionRestoreService = {
  init,
  shouldRestore,
  restore,
  reset,
  cleanup,
};

export default FormVersionRestoreService;
